import {
  FULL_VIDEO_MAX_SEC,
  FULL_VIDEO_MIN_SEC,
  fullVideoLengthSec,
  KitAssetTrim,
} from 'fym-shared/src/music24/fullVideoPlan';
import { areLinesTimed, lyricLinesOf, LyricTimingV1 } from 'fym-shared/src/music24/lyricTiming';
import type { LyricMomentLengthSec } from 'fym-shared/src/music24/studioThemes';
import { lyricMomentDefaultLines } from 'fym-shared/src/music24/story/lyricMomentVideo';

/**
 * The step-3 rules of the full videos and the lyric moment (spec 2026-10-05 §4, §6.1; designs s7,
 * s7b). Pure functions: the Studio has no beat grid, so a trim snaps to whole seconds and the server
 * snaps it to the downbeats.
 */

/** The last second that a full video of the song can reach: the song, at most 900 s. */
export function fullVideoEndSec(durationSec: number): number {
  return Math.floor(Math.min(durationSec, FULL_VIDEO_MAX_SEC));
}

/** True when the song is long enough for a trim that is shorter than the song (more than 30 s). */
export function canTrimSong(durationSec: number | null): boolean {
  return durationSec !== null && fullVideoEndSec(durationSec) > FULL_VIDEO_MIN_SEC;
}

/** The first trim when the artist picks "Trim": the whole song, up to the 900 s cap. */
export function defaultTrim(durationSec: number): KitAssetTrim {
  return { startSec: 0, endSec: fullVideoEndSec(durationSec) };
}

/**
 * Puts a trim inside the song on whole seconds and keeps it at least 30 s long. `edge` is the field
 * that the artist changed: when the trim gets too short, the other edge moves.
 */
export function fitTrim(trim: KitAssetTrim, durationSec: number, edge: 'start' | 'end'): KitAssetTrim {
  const end = fullVideoEndSec(durationSec);
  const lastStart = Math.max(0, end - FULL_VIDEO_MIN_SEC);
  let startSec = Math.min(Math.max(Math.round(trim.startSec), 0), lastStart);
  let endSec = Math.min(Math.max(Math.round(trim.endSec), Math.min(FULL_VIDEO_MIN_SEC, end)), end);
  if (endSec - startSec < FULL_VIDEO_MIN_SEC) {
    if (edge === 'start') {
      endSec = Math.min(startSec + FULL_VIDEO_MIN_SEC, end);
    } else {
      startSec = Math.max(endSec - FULL_VIDEO_MIN_SEC, 0);
    }
  }
  return { startSec, endSec };
}

/** True when the trim cuts the song: the Studio sends `params.trim` only then. */
export function isTrimmed(trim: KitAssetTrim | undefined, durationSec: number | null): trim is KitAssetTrim {
  if (trim === undefined) {
    return false;
  }
  return trim.startSec > 0 || durationSec === null || trim.endSec < fullVideoEndSec(durationSec);
}

/** The length of the full video in seconds: the trim, else the song. Null when the song length is not known. */
export function fullVideoLengthOf(trim: KitAssetTrim | undefined, durationSec: number | null): number | null {
  if (durationSec === null) {
    return isTrimmed(trim, null) ? trim.endSec - trim.startSec : null;
  }
  return fullVideoLengthSec(durationSec, isTrimmed(trim, durationSec) ? trim : null);
}

/** `m:ss` of a song time in whole seconds. */
export function clockTime(seconds: number): string {
  const whole = Math.max(0, Math.round(seconds));
  return `${Math.floor(whole / 60)}:${String(whole % 60).padStart(2, '0')}`;
}

/** `m:ss.s` of a line time in ms (design s7b "0:58.1"). */
export function lineClock(ms: number): string {
  const tenths = Math.max(0, Math.round(ms / 100));
  const minutes = Math.floor(tenths / 600);
  const seconds = (tenths - minutes * 600) / 10;
  return `${minutes}:${seconds < 10 ? '0' : ''}${seconds.toFixed(1)}`;
}

/** Reads "3:54", "234" or "1:02.5" as seconds. Null for any other text. */
export function parseClock(text: string): number | null {
  const match = /^\s*(?:(\d+):)?(\d+(?:\.\d+)?)\s*$/.exec(text);
  if (match === null) {
    return null;
  }
  const minutes = match[1] === undefined ? 0 : Number(match[1]);
  const seconds = Number(match[2]);
  if (match[1] !== undefined && seconds >= 60) {
    return null;
  }
  return minutes * 60 + seconds;
}

/** One row of the line picker of a lyric moment (design s7b). Times are null while the lyrics sync. */
export interface PickLine {
  index: number;
  text: string;
  startMs: number | null;
  endMs: number | null;
  /** A blank line or a section label comes before the line: a stanza starts here. */
  gapBefore?: boolean;
}

/** The rows of the line picker: the synced lines, else the lyric lines with no times. */
export function pickLinesOf(timing: LyricTimingV1 | null, lyrics: string | null): PickLine[] {
  if (timing !== null && timing.lines.length > 0) {
    return timing.lines.map(({ index, text, startMs, endMs, gapBefore }) => ({
      index,
      text,
      startMs,
      endMs,
      gapBefore,
    }));
  }
  return lyricLinesOf(lyrics).map(({ index, text }) => ({ index, text, startMs: null, endMs: null }));
}

/** The first and the last line of a lyric moment (`params.lineFrom`, `params.lineTo`). */
export interface LineRange {
  lineFrom: number;
  lineTo: number;
}

/** The lines that the step selects when the song has no times yet. */
const UNTIMED_LINES = 4;

/**
 * The lines that the step selects when it opens (design `asset-redesign/u`, owner answers 1 and 2 of
 * 2026-10-08): the stanza at the hook (fym-shared `lyricMomentDefaultLines`, the rule of the render
 * samples too), with every next line that starts before the end card of a `lengthSec` moment. With
 * no times (the lyrics sync), the first four lines. Null with no lines.
 */
export function hookLineRange(
  lines: readonly PickLine[],
  hookStartSec: number | null,
  lengthSec: number = 15
): LineRange | null {
  if (lines.length === 0) {
    return null;
  }
  return (
    lyricMomentDefaultLines(lines, hookStartSec, lengthSec) ?? {
      lineFrom: 0,
      lineTo: Math.min(UNTIMED_LINES, lines.length) - 1,
    }
  );
}

/** The indexes of the lines inside the hook window (the "Hook" tag of design s7b). */
export function hookLineIndexes(lines: readonly PickLine[], hookStartSec: number | null): Set<number> {
  const indexes = new Set<number>();
  const range = hookStartSec === null ? null : hookLineRange(lines, hookStartSec);
  if (range === null) {
    return indexes;
  }
  lines.forEach((line) => {
    if (line.startMs !== null && line.index >= range.lineFrom && line.index <= range.lineTo) {
      indexes.add(line.index);
    }
  });
  return indexes;
}

/** The indexes of the lines whose text shows more than once (the "Chorus" tag of design s7b). */
export function chorusLineIndexes(lines: readonly PickLine[]): Set<number> {
  const counts = new Map<string, number>();
  const keyOf = (text: string) => text.trim().toLowerCase();
  lines.forEach((line) => counts.set(keyOf(line.text), (counts.get(keyOf(line.text)) ?? 0) + 1));
  return new Set(lines.filter((line) => (counts.get(keyOf(line.text)) ?? 0) > 1).map((line) => line.index));
}

/** A line with no end shows this long when no later line has a time. */
const LAST_LINE_MS = 4000;

/** The seconds from the start of the first line to the end of the last line. Null when a time is missing. */
export function rangeSpanSec(lines: readonly PickLine[], range: LineRange): number | null {
  const firstStart = lines.find((line) => line.index === range.lineFrom)?.startMs ?? null;
  const last = lines.find((line) => line.index === range.lineTo);
  const lastStart = last?.startMs ?? null;
  if (firstStart === null || last === undefined || lastStart === null) {
    return null;
  }
  const next = lines.find((line) => line.index > range.lineTo && line.startMs !== null);
  const endMs = last.endMs ?? next?.startMs ?? lastStart + LAST_LINE_MS;
  return Math.max(0, endMs - firstStart) / 1000;
}

/** The length of a lyric moment from the span of its lines: 15 s when they fit in 15 s, else 30 s. */
export function momentLengthOf(spanSec: number | null): LyricMomentLengthSec {
  return spanSec !== null && spanSec > 15 ? 30 : 15;
}

/**
 * The next selection after a click on a line (design s7b: click the first line, then the last).
 * `anchor` is the first click; null starts a new selection on the clicked line.
 */
export function rangeAfterClick(anchor: number | null, clicked: number): { range: LineRange; anchor: number | null } {
  if (anchor === null) {
    return { range: { lineFrom: clicked, lineTo: clicked }, anchor: clicked };
  }
  return { range: { lineFrom: Math.min(anchor, clicked), lineTo: Math.max(anchor, clicked) }, anchor: null };
}

/**
 * True when "Make it" can post a lyric moment (design s7b): every picked line has a time
 * (`areLinesTimed`, else create answers 400 `M24_LYRIC_LINES_NOT_TIMED`). While the lyrics sync,
 * "Make it" stays on: the job waits for the times.
 */
export function isMomentReady(input: {
  isSyncing: boolean;
  timing: LyricTimingV1 | null;
  lineFrom: number | undefined;
  lineTo: number | undefined;
}): boolean {
  const { isSyncing, timing, lineFrom, lineTo } = input;
  if (lineFrom === undefined || lineTo === undefined) {
    return false;
  }
  if (isSyncing) {
    return true;
  }
  return timing !== null && areLinesTimed(timing, lineFrom, lineTo);
}
